import type { DicePoolEntry, ParsedNotation } from '../../../hooks/useDiceRoller';
import type { DiceGroup } from '../DiceRollerOverlay';

export type RollModeOption = 'normal' | 'advantage' | 'disadvantage';

export interface HistoryEntry {
  notation: string;
  total: number;
  timestamp: number;
}

/**
 * Roll a single die (returns 1 to sides)
 */
export function rollDie(sides: number): number {
  return Math.floor(Math.random() * sides) + 1;
}

/**
 * Pre-roll dice and create DiceGroup objects with predetermined values
 */
export function preRollDiceGroups(pool: DicePoolEntry[]): DiceGroup[] {
  return pool.map(entry => {
    const values: number[] = [];
    for (let i = 0; i < entry.count; i++) {
      values.push(rollDie(entry.sides));
    }
    return {
      count: entry.count,
      sides: entry.sides,
      values,
      keep: entry.keep,
      keepHighest: entry.keepHighest,
    };
  });
}

/**
 * Build notation string including kh/kl for advantage and explicit keep settings
 */
export function buildNotationFromPool(pool: DicePoolEntry[], mod: number, mode: RollModeOption): string {
  if (pool.length === 0 && mod === 0) return '';

  const parts = pool.map((d) => {
    // When advantage/disadvantage is set and we have a single d20, show it as 2d20kh1/kl1
    if (d.sides === 20 && d.count === 1 && mode !== 'normal') {
      return mode === 'advantage' ? '2d20kh1' : '2d20kl1';
    }
    // If keep settings are specified, include them in notation
    if (d.keep !== undefined && d.keep < d.count) {
      const keepSuffix = d.keepHighest ? `kh${d.keep}` : `kl${d.keep}`;
      return `${d.count}d${d.sides}${keepSuffix}`;
    }
    return `${d.count}d${d.sides}`;
  });

  let notation = parts.join('+');
  if (mod !== 0) notation += mod > 0 ? `+${mod}` : `${mod}`;
  return notation;
}

/**
 * Parse notation and detect advantage/disadvantage, preserving keep settings
 */
export function parseNotationToPoolState(parsed: ParsedNotation): {
  pool: DicePoolEntry[];
  modifier: number;
  rollMode: RollModeOption | null;
} {
  let detectedMode: RollModeOption | null = null;

  const normalizedPool = parsed.pool.map((entry) => {
    // Detect 2d20kh1 = advantage, 2d20kl1 = disadvantage (special case for d20s)
    if (entry.sides === 20 && entry.count === 2 && entry.keep === 1) {
      detectedMode = entry.keepHighest ? 'advantage' : 'disadvantage';
      // Normalize to 1d20 in pool (the mode handles the 2d20 behavior)
      return { sides: 20, count: 1 };
    }
    // Preserve keep settings for other dice
    const poolEntry: DicePoolEntry = { sides: entry.sides, count: entry.count };
    if (entry.keep !== undefined) {
      poolEntry.keep = entry.keep;
      poolEntry.keepHighest = entry.keepHighest;
    }
    return poolEntry;
  });

  return { pool: normalizedPool, modifier: parsed.modifier, rollMode: detectedMode };
}
